import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ${
      checked ? 'bg-primary' : 'bg-muted'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const NotificationSection = ({ teacher, onUpdate }) => {
  const [rewardAlerts, setRewardAlerts] = useState(teacher?.notify_reward_redemptions ?? true);
  const [activityAlerts, setActivityAlerts] = useState(teacher?.notify_student_activity ?? false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const { error } = await supabase
        ?.from('teachers')
        ?.update({
          notify_reward_redemptions: rewardAlerts,
          notify_student_activity: activityAlerts,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', teacher?.id);
      if (error) throw error;
      setMsg({ type: 'success', text: 'Notification preferences saved.' });
      onUpdate?.({ notify_reward_redemptions: rewardAlerts, notify_student_activity: activityAlerts });
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Failed to save preferences.' });
    } finally {
      setSaving(false);
    }
  };

  const prefs = [
    {
      label: 'Reward Redemption Alerts',
      description: 'Get notified in real-time when a student redeems a reward.',
      value: rewardAlerts,
      setter: setRewardAlerts,
      icon: 'Gift',
    },
    {
      label: 'Student Activity Notifications',
      description: 'Receive updates when students complete trails or earn stars.',
      value: activityAlerts,
      setter: setActivityAlerts,
      icon: 'Activity',
    },
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
          <Icon name="Bell" size={20} className="text-warning" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Notification Preferences</h2>
          <p className="text-sm text-muted-foreground">Choose which events trigger notifications for you</p>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        {prefs?.map(({ label, description, value, setter, icon }) => (
          <div key={label} className="flex items-start justify-between gap-4 p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-card flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name={icon} size={16} className="text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
              </div>
            </div>
            <Toggle checked={value} onChange={setter} disabled={saving} />
          </div>
        ))}
      </div>

      {msg && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-4 ${
          msg?.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
        }`}>
          <Icon name={msg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
          {msg?.text}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors"
      >
        {saving ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Save" size={16} />}
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>
    </div>
  );
};

export default NotificationSection;
